import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { pick } from "lodash";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { IRoleDetails } from "services/KnowfullyAdminClient";
import ClaimSelector from "./ClaimSelector";

export type RoleFormValues = {
  name?: string;
  description?: string;
  claimIds: string[];
};

const { Form } = withTypes<RoleFormValues>();

type RoleFormProps = {
  role?: IRoleDetails;
  submitLabel: string;
  onSubmit: (values: RoleFormValues) => void;
  onCancel?: () => void;
};

const RoleForm = ({
  role,
  submitLabel,
  onCancel,
  onSubmit,
}: RoleFormProps) => {
  const { formatMessage: t } = useIntl();

  const init = useMemo<RoleFormValues>(
    () => ({
      claimIds: [],
      ...pick(role ?? {}, ["claimIds", "description", "name"]),
    }),
    [role]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({
        handleSubmit,
        submitting,
        submitError,
      }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        disabled={!!role}
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "descriptionLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="claimIds">
                  {({ input }) => (
                    <Grid item xs={12}>
                      <ClaimSelector
                        selectedIds={input.value}
                        onChange={input.onChange}
                        isCreateMode={!role}
                      />
                    </Grid>
                  )}
                </Field>
                {submitError && (
                  <Box width="100%">
                    <Alert severity="error">{submitError}</Alert>
                  </Box>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={submitting}
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    />
  );
};

export default RoleForm;
